import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import {
  CanvasSidebarStartUpdateEventDetail,
  PorscheDesignSystemModule,
} from '@porsche-design-system/components-angular';

import { AuthService } from './core/auth/auth.service';
import { SessionStatusBanner } from './core/auth/session-status-banner';
import { Domain } from './core/auth/user-access.model';
import { UserAccessService } from './core/auth/user-access.service';
import { EnvironmentService } from './core/environment/environment.service';
import { ThemeService } from './core/theme/theme.service';

interface SearchEntry {
  readonly label: string;
  readonly description: string;
  readonly route: string;
  readonly domain?: Domain;
  readonly globalAdminOnly?: boolean;
}

@Component({
  selector: 'app-root',
  imports: [
    PorscheDesignSystemModule,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    SessionStatusBanner,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  protected readonly access = inject(UserAccessService);
  protected readonly environmentService = inject(EnvironmentService);
  protected readonly environmentColor = computed(
    () =>
      (
        ({
          local: 'background-surface',
          dev: 'notification-info-soft',
          staging: 'notification-warning-soft',
          prod: 'notification-success-soft',
        }) as const
      )[this.environmentService.targetEnvironment()],
  );
  protected readonly sidebarStartOpen = signal(true);
  protected readonly sidebarEndOpen = signal(false);
  protected readonly searchOpen = signal(false);
  protected readonly searchTerm = signal('');
  private readonly searchEntries: readonly SearchEntry[] = [
    { label: 'Health', description: 'Operations', route: '/health', domain: 'OPERATIONS' },
    {
      label: 'Manage Configurations',
      description: 'SASI Admin',
      route: '/manage-configurations',
      domain: 'SASI',
    },
    {
      label: 'Display New Car Order',
      description: 'SASI Admin',
      route: '/display-pvms',
      domain: 'SASI',
    },
    {
      label: 'Stocks',
      description: 'Stocklocator',
      route: '/stocklocator/stocks',
      domain: 'STOCKLOCATOR',
    },
    {
      label: 'BINs',
      description: 'Stocklocator',
      route: '/stocklocator/bins',
      domain: 'STOCKLOCATOR',
    },
    {
      label: 'User Management',
      description: 'Administration',
      route: '/user-management',
      globalAdminOnly: true,
    },
    { label: 'Settings', description: 'Account', route: '/settings' },
    { label: 'Help', description: 'Account', route: '/help' },
    { label: 'Account', description: 'Account', route: '/account' },
    {
      label: 'Inventory Assessment',
      description: 'GPP Excess Return',
      route: '/excess-return',
      domain: 'EXCESS_RETURN',
    },
    {
      label: 'Manage Configurations',
      description: 'GPP Excess Return - Excess Return Calculation Parameters',
      route: '/excess-return/manage-configurations',
      domain: 'EXCESS_RETURN_ADMIN',
    },
    {
      label: 'Central Routing',
      description: 'Central Routing',
      route: '/central-routing',
      domain: 'CENTRAL_ROUTING',
    },
  ] as const;
  protected readonly searchResults = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    return this.searchEntries
      .filter((entry) => this.canSearchEntry(entry))
      .filter((entry) => {
        if (!term) {
          return true;
        }

        return [entry.label, entry.description, entry.route].some((value) =>
          value.toLowerCase().includes(term),
        );
      });
  });
  protected readonly profileName = computed(() => {
    const user = this.authService.userInformation();
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    const username = user?.userId?.trim();
    if (fullName && username) {
      return `${fullName} (${username})`;
    }
    return fullName || username || this.access.currentUser()?.name || 'AsPIRe Cockpit user';
  });
  protected readonly profileInitials = computed(
    () =>
      this.profileName()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('') || 'AS',
  );
  protected readonly profileRole = computed(() =>
    this.access.isGlobalAdmin() ? 'Global Admin' : 'User',
  );

  protected openSearch(): void {
    this.searchOpen.set(true);
    this.searchTerm.set('');
  }

  protected closeSearch(): void {
    this.searchOpen.set(false);
    this.searchTerm.set('');
  }

  protected onSearchTermInput(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }

    const [firstResult] = this.searchResults();
    if (!firstResult) {
      return;
    }

    void this.navigateToSearchResult(firstResult);
  }

  protected openSidebarEnd(): void {
    this.sidebarEndOpen.set(true);
  }

  protected closeSidebarEnd(): void {
    this.sidebarEndOpen.set(false);
  }

  protected logout(): void {
    void this.authService.logout();
  }

  protected updateSidebarStart(event: CustomEvent<CanvasSidebarStartUpdateEventDetail>): void {
    this.sidebarStartOpen.set(event.detail.open);
  }

  protected async navigateToSearchResult(entry: SearchEntry): Promise<void> {
    await this.router.navigateByUrl(entry.route);
    this.closeSearch();
  }

  private canSearchEntry(entry: SearchEntry): boolean {
    if (entry.globalAdminOnly) {
      return this.access.isGlobalAdmin();
    }

    if (entry.domain) {
      return this.access.canAccess(entry.domain);
    }

    return true;
  }
}
